<template>
    <form
        class="external-modal-form"
        @submit.prevent="onSubmit"
    >
        <model-icon
            v-if="!newModel"
            editable
            :url.sync="localModel.img"
            :default-image="selectedEngine.image"
        />
        <or-textbox
            v-model="$v.localModel.name.$model"
            :label="$t('common.modals.create_external.external-model-form.name.label')"
            :placeholder="$t('common.modals.create_external.external-model-form.name.placeholder')"
            enforceMaxlength
            :invalid="!!errors.name"
            :error="errors.name"
            :maxlength="sizeLimits.title.max"
        />
        <or-textbox
            v-model="localModel.description"
            :label="$t('common.modals.create_external.external-model-form.description.label')"
            :placeholder="$t('common.modals.create_external.external-model-form.description.placeholder')"
            enforceMaxlength
            :rows="4"
            :multi-line="true"
            :maxlength="sizeLimits.description.max"
        />
        <or-select
            type="image"
            :label="$t('common.modals.create_external.external-model-form.engine.label')"
            :placeholder="$t('common.modals.create_external.external-model-form.engine.placeholder')"
            :value="selectedEngine"
            :options="customEngines"
            :invalid="!!errors.selectedEngine"
            :error="errors.selectedEngine"
            @select="selectEngine"
        />

        <!-- engines -->
        <ms-luis
            v-if="showLuisSetting"
            :luisModel.sync="$v.localModel.$model"
            :errors="errors"
        />
        <ms-qna
            v-if="showMsQnaSettings"
            :ms-qna-model.sync="$v.localModel.$model"
            :errors="errors"
        />
        <dialog-flow
            v-if="showDialogflowSetting"
            :dialog-flow-model.sync="$v.localModel.$model"
            :errors="errors"
        />
        <aws-comprehend
            v-if="showComprehendSetting"
            :aws-comprehend-model.sync="$v.localModel.$model"
            :errors="errors"
        />
        <aws-lex
            v-if="showLexSetting"
            :aws-lex-model.sync="$v.localModel.$model"
            :errors="errors"
        />
        <rasa
            v-if="showRasaSetting"
            :rasaModel.sync="$v.localModel.$model"
            :errors="errors"
        />
        <service-now
            v-if="showServiceNowSettings"
            :serviceNowModel.sync="$v.localModel.$model"
            :errors="errors"
        />

        <slot
          name="actions"
          v-bind:submit="onSubmit"
          v-bind:cancel="onCancel"
        />
    </form>
</template>

<script>
import { mapState } from 'vuex'
import { isValidJSON } from '@/utils/validations'
import { meta_limits as metaLimits } from '@/modules/limits'
import ModelIcon from '@/components/model/common/model_icon'

import { externalModelValidation } from '@/mixins/models-validation.mixin'

import MsLuis, { getDefaultLuisSettings } from './engines/ms-luis'
import MsQna, { getDefaultMsQnaSettings } from './engines/ms-qna'
import DialogFlow, { getDefaultDialogFlowSettings } from './engines/dialog-flow'
import Rasa, { getDefaultRasaSettings } from './engines/rasa'
import AwsComprehend, { getAwsComprehendDefaultSettings } from './engines/aws-comprehend'
import AwsLex, { getAwsLexDefaultSettings } from './engines/aws-lex'
import ServiceNow, { getServiceNowDefaultSettings } from './engines/service-now'

const parseExternalModel = externalModel => {
  const localModel = _.cloneDeep(externalModel) || {}
  // Supporting initial versions as they didn't have description in them
  if (!localModel.img) {
    localModel.img = ''
  }
  if (!localModel.description) {
    localModel.description = ''
  }

  // Settings objects should be saved as strings so that we can edit them in editor|validate incorrect values(parsing errors)
  if (localModel.settings) {
    if (localModel.settings.bodyTemplate) {
      localModel.settings.bodyTemplate = JSON.stringify(localModel.settings.bodyTemplate)
    }
    if (localModel.settings.queryParamsTemplate) {
      localModel.settings.queryParamsTemplate = JSON.stringify(localModel.settings.queryParamsTemplate)
    }
    if (localModel.settings.customHeaders) {
      localModel.settings.customHeaders = JSON.stringify(localModel.settings.customHeaders)
    }
  }
  return localModel
}

const parseLocalModel = localModel => {
  const externalModel = _.cloneDeep(localModel)

  if (externalModel.settings) {
    if (isValidJSON(externalModel.settings.bodyTemplate)) {
      externalModel.settings.bodyTemplate = JSON.parse(externalModel.settings.bodyTemplate)
    }
    if (isValidJSON(externalModel.settings.queryParamsTemplate)) {
      externalModel.settings.queryParamsTemplate = JSON.parse(externalModel.settings.queryParamsTemplate)
    }
    if (isValidJSON(externalModel.settings.customHeaders)) {
      externalModel.settings.customHeaders = JSON.parse(externalModel.settings.customHeaders)
    }
  }

  return externalModel
}

export default {
  mixins: [externalModelValidation],

  components: {
    ModelIcon,
    MsLuis,
    MsQna,
    DialogFlow,
    Rasa,
    AwsComprehend,
    AwsLex,
    ServiceNow
  },
  props: {
    externalModel: Object,
    isLoading: Boolean,
    newModel: Boolean
  },

  data () {
    return {
      localModel: parseExternalModel(this.$props.externalModel)
    }
  },

  computed: {
    ...mapState('knowledgebase/customModels', ['customEngines']),

    selectedEngine () {
      return this.customEngines.find(engine => engine.id === this.localModel.engine) || {}
    },

    sizeLimits () {
      return metaLimits
    },

    showLuisSetting () {
      return this.localModel.engine === 'luis'
    },

    showMsQnaSettings () {
      return this.localModel.engine === 'msQna'
    },

    showDialogflowSetting () {
      return this.localModel.engine === 'dialogflow'
    },

    showComprehendSetting () {
      return this.localModel.engine === 'comprehend'
    },

    showLexSetting () {
      return this.localModel.engine === 'lex'
    },

    showRasaSetting () {
      return this.localModel.engine === 'rasa'
    },

    showServiceNowSettings () {
      return this.localModel.engine === 'serviceNow'
    }
  },

  methods: {
    selectEngine (selectedEngine) {
      if (selectedEngine.id === this.localModel.engine) return

      if (selectedEngine.id === this.$props.externalModel.engine) {
        this.localModel = parseExternalModel(this.$props.externalModel)
      } else {
        this.localModel = this.getDefaultModel(selectedEngine.id)
      }
    },

    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      this.$emit('submit', parseLocalModel(this.localModel))
    },

    onCancel () {
      this.$emit('cancel')
      this.$v.$reset()
    },

    getDefaultModel (selectedEngineId) {
      switch (selectedEngineId) {
        case 'luis': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getDefaultLuisSettings()
          }
        }

        case 'msQna': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getDefaultMsQnaSettings()
          }
        }

        case 'dialogflow': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getDefaultDialogFlowSettings()
          }
        }

        case 'rasa': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getDefaultRasaSettings()
          }
        }

        case 'comprehend': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getAwsComprehendDefaultSettings()
          }
        }

        case 'lex': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getAwsLexDefaultSettings()
          }
        }

        case 'serviceNow': {
          return {
            ...this.localModel,
            engine: selectedEngineId,
            settings: getServiceNowDefaultSettings()
          }
        }
      }

      return this.localModel
    }
  },

  watch: {
    externalModel: {
      handler (value) {
        this.localModel = parseExternalModel(value)
      },
      deep: true
    }
  }
}

</script>

<style lang="scss">
    .external-modal-form {
        margin-bottom: $sp-2;
        .settings-wrap {
            border: solid 1px $or-grey-medium;
            border-radius: 4px;
            padding: $sp-3;
            margin: $sp-5 0 $sp-3;
        }
        .engine {
            .label {
                font-size: 14px;
                font-weight: bold;
                line-height: 16px;
            }
            .engine--description {
                margin: 16px 0;
                color: rgb(145, 150, 157);
                font-size: 14px;
                font-weight: normal;
                letter-spacing: -0.15px;
                line-height: 21px;
            }

            .engine--info-url {
                text-decoration: none;
                color: rgb(68, 131, 236);
                &:visited, &:hover, &:active {
                    text-decoration: none;
                    color: rgb(68, 131, 236);
                }
            }
        }
    }
</style>
